import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Typography, message, Tabs, Alert } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const { Title, Text } = Typography;

export const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { login, register, isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const onLoginFinish = async (values: { email: string; password: string }) => {
    setLoading(true);
    setError(null);
    try {
      await login(values.email, values.password);
      message.success('Login successful!');
        navigate('/dashboard');
    } catch (error: any) {
      console.error('Login error details:', error);
      let errorMessage = 'Login failed';
      
      if (error.response) {
        // Server responded with error status
        errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        // Something else happened
        errorMessage = error.message || 'Unknown error occurred';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const onLoginFinishFailed = (errorInfo: any) => {
    console.log('Login form validation failed:', errorInfo);
    setError('Please fill in all required fields correctly');
  };

  const onRegisterFinish = async (values: {
    name: string;
    email: string;
    password: string;
    confirmPassword: string;
  }) => {
    if (values.password !== values.confirmPassword) {
      setError('Password confirmation does not match');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await register(values.name, values.email, values.password, values.confirmPassword);
      message.success('Registration successful!');
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Registration error details:', error);
      let errorMessage = 'Registration failed';
      
      if (error.response) {
        // Server responded with error status
        errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        // Something else happened
        errorMessage = error.message || 'Unknown error occurred';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const onRegisterFinishFailed = (errorInfo: any) => {
    console.log('Register form validation failed:', errorInfo);
    setError('Please fill in all required fields correctly');
  };

  return (
    <>
      <style>
        {`
          * {
            box-sizing: border-box;
          }
          
          body {
            margin: 0;
            padding: 0;
            overflow-x: hidden;
          }
        `}
      </style>
      <div style={{ 
        height: '100vh', 
        width: '100vw',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: '#f5f5f5',
      }}>
        <div style={{
          background: 'white',
          borderRadius: '8px',
          padding: '40px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          maxWidth: '450px',
          width: '100%',
        }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <Title level={2} style={{ marginBottom: '8px', color: '#262626' }}>
              Admin Login
            </Title>
            <Text style={{ color: '#8c8c8c', fontSize: '16px' }}>
              Sign in to your account
            </Text>
          </div>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
              style={{ marginBottom: '24px' }}
            />
          )}

          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            size="large"
            items={[
              {
                key: 'login',
                label: 'Login',
                children: (
                  <Form
                    name="login"
                    onFinish={onLoginFinish}
                    onFinishFailed={onLoginFinishFailed}
                    autoComplete="off"
                    size="large"
                    layout="vertical"
                  >
                    <Form.Item
                      name="email"
                      label="Email"
                      rules={[
                        { required: true, message: 'Please enter your email' },
                        { type: 'email', message: 'Please enter a valid email' },
                      ]}
                    >
                      <Input
                        placeholder="Enter your email"
                        style={{ height: '48px', fontSize: '16px' }}
                      />
                    </Form.Item>

                    <Form.Item
                      name="password"
                      label="Password"
                      rules={[{ required: true, message: 'Please enter your password' }]}
                    >
                      <Input.Password
                        placeholder="Enter your password"
                        style={{ height: '48px', fontSize: '16px' }}
                      />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: '16px' }}>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        style={{ 
                          width: '100%', 
                          height: '48px',
                          fontSize: '16px',
                          fontWeight: 'bold'
                        }}
                      >
                        Login
                      </Button>
                    </Form.Item>
                  </Form>
                ),
              },
              {
                key: 'register',
                label: 'Register',
                children: (
                  <Form
                    name="register"
                    onFinish={onRegisterFinish}
                    onFinishFailed={onRegisterFinishFailed}
                    autoComplete="off"
                    size="large"
                    layout="vertical"
                  >
                    <Form.Item
                      name="name"
                      label="Name"
                      rules={[{ required: true, message: 'Please enter your name' }]}
                    >
                      <Input
                        placeholder="Enter your name"
                        style={{ height: '48px', fontSize: '16px' }}
                      />
                    </Form.Item>

                    <Form.Item
                      name="email"
                      label="Email"
                      rules={[
                        { required: true, message: 'Please enter your email' },
                        { type: 'email', message: 'Please enter a valid email' },
                      ]}
                    >
                      <Input
                        placeholder="Enter your email"
                        style={{ height: '48px', fontSize: '16px' }}
                      />
                    </Form.Item>

                    <Form.Item
                      name="password"
                      label="Password"
                      rules={[
                        { required: true, message: 'Please enter your password' },
                        { min: 6, message: 'Password must be at least 6 characters' },
                      ]}
                    >
                      <Input.Password
                        placeholder="Enter your password"
                        style={{ height: '48px', fontSize: '16px' }}
                      />
                    </Form.Item>

                    <Form.Item
                      name="confirmPassword"
                      label="Confirm Password"
                      rules={[{ required: true, message: 'Please confirm your password' }]}
                    >
                      <Input.Password
                        placeholder="Enter your password again"
                        style={{ height: '48px', fontSize: '16px' }}
                      />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: '16px' }}>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        style={{ 
                          width: '100%', 
                          height: '48px',
                          fontSize: '16px',
                          fontWeight: 'bold'
                        }}
                      >
                        Register
                      </Button>
                    </Form.Item>
                  </Form>
                ),
              },
            ]}
          />

          <div style={{ textAlign: 'center', color: '#8c8c8c', fontSize: '14px', marginTop: '24px' }}>
            By creating an account, you agree to our
            <a href="/privacy" style={{ color: '#1890ff' }}> Privacy Policy</a>
            and
            <a href="/terms" style={{ color: '#1890ff' }}> Terms of Service</a>
          </div>
        </div>
      </div>
    </>
  );
};
